import {
  Dropdown,
  DropdownOption,
} from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import { CompanyBankAccountType } from "model/Company";
import {
  isPaymentMethodValueValidOrEmpty,
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "web-apps/company/components/staff/components/shared/staff.paymentMethod.logic";
import {
  PassSettingControl,
  PassSettingControls,
} from "./passSettingsV2.styled";
import {
  SystemSettingsInfoBox,
  SystemSettingsInfoContent,
  SystemSettingsInfoText,
  SystemSettingsSectionDescription,
  SystemSettingsSectionHeader,
  SystemSettingsSectionTitle,
  SystemSettingsSectionTitleRow,
} from "./companySystemSettings.styled";
import { SystemSettingRow } from "./systemSettingRow.component";

interface CompanyBankAccountSettingProps {
  readonly agreementId: string;
  readonly accountType: CompanyBankAccountType;
  readonly accountValue: string;
  readonly onChangeAgreementId: (value: string) => void;
  readonly onChangeAccountType: (value: CompanyBankAccountType) => void;
  readonly onChangeAccountValue: (value: string) => void;
}

const MAX_BANK_AGREEMENT_ID_LENGTH = 255;
const MAX_BANK_ACCOUNT_LENGTH = 15;
const MAX_IBAN_LENGTH = 34;

function getBankAccountErrorText({
  accountType,
  accountValue,
  translate,
}: {
  readonly accountType: CompanyBankAccountType;
  readonly accountValue: string;
  readonly translate: (key: string) => string;
}): string {
  if (isPaymentMethodValueValidOrEmpty(accountType, accountValue)) {
    return "";
  }
  const validationResult = validatePaymentMethodValue(
    accountType,
    accountValue,
  );
  return validationResult.type === "failure"
    ? translate(toPaymentMethodValidationTranslationKey(validationResult.error))
    : "";
}

export const CompanyBankAccountSetting = ({
  agreementId,
  accountType,
  accountValue,
  onChangeAgreementId,
  onChangeAccountType,
  onChangeAccountValue,
}: CompanyBankAccountSettingProps): JSX.Element => {
  const { t } = useTranslation();
  const accountValueDescriptionKey =
    accountType === "InternationalBankAccount"
      ? "System.CompanyBankAccountValueInfoInternationalBankAccount"
      : "System.CompanyBankAccountValueInfoBankAccount";
  const accountValueMaxLength =
    accountType === "InternationalBankAccount"
      ? MAX_IBAN_LENGTH
      : MAX_BANK_ACCOUNT_LENGTH;
  const accountValueErrorText = getBankAccountErrorText({
    accountType,
    accountValue,
    translate: t,
  });

  const accountTypeOptions: DropdownOption[] = [
    {
      label: t("System.BankAccountTypeOptionBankAccount"),
      value: "BankAccount",
    },
    {
      label: t("System.BankAccountTypeOptionInternationalBankAccount"),
      value: "InternationalBankAccount",
    },
  ];

  const handleAccountTypeChange = (value: string): void => {
    onChangeAccountType(
      value === "InternationalBankAccount"
        ? "InternationalBankAccount"
        : "BankAccount",
    );
  };

  return (
    <>
      <SystemSettingsSectionHeader>
        <SystemSettingsSectionTitleRow>
          <SystemSettingsSectionTitle>
            {t("System.BankSettingsTitle")}
          </SystemSettingsSectionTitle>
        </SystemSettingsSectionTitleRow>
        <SystemSettingsSectionDescription>
          {t("System.BankSettingsDescription")}
        </SystemSettingsSectionDescription>
      </SystemSettingsSectionHeader>

      <SystemSettingRow
        title={t("System.CompanyBankAgreementId")}
        description={t("System.CompanyBankAgreementIdInfo")}
      >
        <PassSettingControls>
          <PassSettingControl>
            <InputField
              ariaLabel={t("System.CompanyBankAgreementId")}
              maxLength={MAX_BANK_AGREEMENT_ID_LENGTH}
              value={agreementId}
              onChange={(value) =>
                onChangeAgreementId(
                  value.slice(0, MAX_BANK_AGREEMENT_ID_LENGTH),
                )
              }
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingRow
        title={t("System.CompanyBankAccountType")}
        description={t("System.CompanyBankAccountTypeInfo")}
        controlVerticalAlign="center"
      >
        <PassSettingControls>
          <PassSettingControl>
            <Dropdown
              ariaLabel={t("System.CompanyBankAccountType")}
              options={accountTypeOptions}
              value={accountType}
              onChange={handleAccountTypeChange}
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingRow
        title={t("System.CompanyBankAccountValue")}
        description={t(accountValueDescriptionKey)}
      >
        <PassSettingControls>
          <PassSettingControl>
            <InputField
              ariaLabel={t("System.CompanyBankAccountValue")}
              errorText={accountValueErrorText}
              hasError={accountValueErrorText.length > 0}
              maxLength={accountValueMaxLength}
              value={accountValue}
              onChange={(value) =>
                onChangeAccountValue(value.slice(0, accountValueMaxLength))
              }
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingsInfoBox>
        <CustomIcon name="v2/info" size="20px" color={ColorV2.Blue4} />
        <SystemSettingsInfoContent>
          <SystemSettingsInfoText>
            {t("System.BankSettingsInfo")}
          </SystemSettingsInfoText>
        </SystemSettingsInfoContent>
      </SystemSettingsInfoBox>
    </>
  );
};
